/**
 * HTX (Huobi) WebSocket Configuration
 * @see https://huobiapi.github.io/docs/spot/v1/en/#websocket-market-data
 */

import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  HtxWsBboMessage,
  HtxWsDepthMessage,
  HtxWsKlineMessage,
  HtxWsPing,
  HtxWsPong,
  HtxWsStatus,
  HtxWsSubscribeRequest,
  HtxWsSubscribeResponse,
  HtxWsTickerMessage,
  HtxWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const HTX_WS_BASE_URL = 'wss://api.huobi.pro/ws';

export const HTX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: 'market.<symbol>.depth.step0',
    supportsBatching: false,
    docUrl: 'https://huobiapi.github.io/docs/spot/v1/en/#market-depth',
  },
  ticker: {
    channel: 'ticker',
    topic: 'market.<symbol>.ticker',
    supportsBatching: false,
    docUrl: 'https://huobiapi.github.io/docs/spot/v1/en/#market-ticker',
  },
  klines: {
    channel: 'klines',
    topic: 'market.<symbol>.kline.<period>',
    supportsBatching: false,
    docUrl: 'https://huobiapi.github.io/docs/spot/v1/en/#kline',
  },
  trades: {
    channel: 'trades',
    topic: 'market.<symbol>.trade.detail',
    supportsBatching: false,
    docUrl: 'https://huobiapi.github.io/docs/spot/v1/en/#trade-detail',
  },
  bookTicker: {
    channel: 'bookTicker',
    topic: 'market.<symbol>.bbo',
    supportsBatching: false,
    docUrl: 'https://huobiapi.github.io/docs/spot/v1/en/#best-bid-offer',
  },
};

export const HTX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: HTX_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 0, // Server-initiated ping/pong
  note: 'All messages are gzip-compressed. Server sends ping frames that must be acknowledged with pong. Orderbook is snapshot-based (not incremental).',
};

export async function decodeHtxWsMessage(
  event: MessageEvent<ArrayBuffer | Blob | string>,
): Promise<unknown> {
  if (typeof event.data === 'string') {
    return JSON.parse(event.data);
  }

  try {
    const buffer = event.data instanceof Blob ? await event.data.arrayBuffer() : event.data;
    const text = pako.ungzip(new Uint8Array(buffer), { to: 'string' });
    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`HTX WebSocket decode error: ${message}`);
  }
}

export interface HtxWsSubscriptionOptions<T> {
  topic: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
  baseUrl?: string;
  wsConfig?: { maxRetries?: number; minReconnectionDelay?: number };
}

function createHtxWsSubscription<T>(options: HtxWsSubscriptionOptions<T>): ReconnectingWebSocket {
  const {
    topic,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = HTX_WS_BASE_URL,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);
  ws.binaryType = 'arraybuffer';

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');
    const subscribeMsg: HtxWsSubscribeRequest = { sub: topic, id: `sub_${Date.now()}` };
    ws.send(JSON.stringify(subscribeMsg));
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    void (async () => {
      try {
        const payload = await decodeHtxWsMessage(event);

        if ((payload as HtxWsPing).ping) {
          const pongMsg: HtxWsPong = { pong: (payload as HtxWsPing).ping };
          ws.send(JSON.stringify(pongMsg));
          return;
        }

        const subResponse = payload as HtxWsSubscribeResponse;
        if (subResponse.status === 'ok' && subResponse.subbed) {
          return;
        }

        if (subResponse.status === 'error' && subResponse['err-code']) {
          onError?.(
            new Error(
              `HTX subscription error: ${subResponse['err-code']} - ${subResponse['err-msg']}`,
            ),
          );
          return;
        }

        onMessage(payload as T);
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        onError?.(err);
        onStatusChange?.('error');
      }
    })();
  });

  ws.addEventListener('close', () => {
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    onError?.(new Error(`HTX WebSocket error: ${event.type}`));
    onStatusChange?.('error');
  });

  return ws;
}

export function subscribeHtxKlines(options: {
  symbol: string;
  period: string;
  onMessage: (kline: HtxWsKlineMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
}): ReconnectingWebSocket {
  return createHtxWsSubscription<HtxWsKlineMessage>({
    topic: `market.${options.symbol}.kline.${options.period}`,
    onMessage: options.onMessage,
    onError: options.onError,
    onStatusChange: options.onStatusChange,
  });
}

export function subscribeHtxDepth(options: {
  symbol: string;
  step?: string;
  onMessage: (depth: HtxWsDepthMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
}): ReconnectingWebSocket {
  return createHtxWsSubscription<HtxWsDepthMessage>({
    topic: `market.${options.symbol}.depth.${options.step ?? 'step0'}`,
    onMessage: options.onMessage,
    onError: options.onError,
    onStatusChange: options.onStatusChange,
  });
}

export function subscribeHtxTrades(options: {
  symbol: string;
  onMessage: (trade: HtxWsTradeMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
}): ReconnectingWebSocket {
  return createHtxWsSubscription<HtxWsTradeMessage>({
    topic: `market.${options.symbol}.trade.detail`,
    onMessage: options.onMessage,
    onError: options.onError,
    onStatusChange: options.onStatusChange,
  });
}

export function subscribeHtxTicker(options: {
  symbol: string;
  onMessage: (ticker: HtxWsTickerMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
}): ReconnectingWebSocket {
  return createHtxWsSubscription<HtxWsTickerMessage>({
    topic: `market.${options.symbol}.ticker`,
    onMessage: options.onMessage,
    onError: options.onError,
    onStatusChange: options.onStatusChange,
  });
}

export function subscribeHtxBbo(options: {
  symbol: string;
  onMessage: (bbo: HtxWsBboMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: HtxWsStatus) => void;
}): ReconnectingWebSocket {
  return createHtxWsSubscription<HtxWsBboMessage>({
    topic: `market.${options.symbol}.bbo`,
    onMessage: options.onMessage,
    onError: options.onError,
    onStatusChange: options.onStatusChange,
  });
}
